// The body of a lead landing screen: the plan cards for what was created
// and the numbered "what happens next" steps. The page supplies the
// heading and actions.

import { Link } from "react-router-dom";

export interface LeadLandingCard {
  kicker: string;
  pillLabel: string;
  title: string;
  sub?: string;
  link?: { to: string; label: string };
}

export interface LeadLandingStep {
  title: string;
  body: string;
}

function PlanCard({ card }: { card: LeadLandingCard }) {
  return (
    <div className="flex flex-col gap-1.5 rounded-2xl border border-oat-200 bg-white p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-700">
          {card.kicker}
        </span>
        <span className="inline-flex items-center whitespace-nowrap rounded-full bg-cantelope-200 px-3 py-1 text-[13px] font-bold text-soil">
          {card.pillLabel}
        </span>
      </div>
      <span className="font-serif text-[22px] leading-tight text-soil">
        {card.title}
      </span>
      {card.sub && (
        <span className="text-base leading-normal text-oat-700">
          {card.sub}
        </span>
      )}
      {card.link && (
        <Link
          to={card.link.to}
          className="mt-1 font-bold text-evergreen underline underline-offset-4 hover:text-plum"
        >
          {card.link.label}
        </Link>
      )}
    </div>
  );
}

export default function LeadLanding({
  cards,
  steps,
}: {
  cards: LeadLandingCard[];
  steps: LeadLandingStep[];
}) {
  return (
    <div className="mt-8 flex flex-col gap-6">
      {cards.length > 0 && (
        <div className="flex flex-col gap-4">
          {cards.map((card) => (
            <PlanCard key={`${card.kicker}-${card.title}`} card={card} />
          ))}
        </div>
      )}
      {steps.length > 0 && (
        <section aria-labelledby="lead-landing-next">
          <h2
            id="lead-landing-next"
            className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-700"
          >
            What happens next
          </h2>
          <ol className="mt-3 flex flex-col gap-4">
            {steps.map((step, index) => (
              <li key={step.title} className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-evergreen text-sm font-bold text-daisy"
                >
                  {index + 1}
                </span>
                <span className="flex flex-col">
                  <span className="text-lg font-bold text-soil">
                    {step.title}
                  </span>
                  <span className="text-base leading-normal text-oat-700">
                    {step.body}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
